import React, { useState } from 'react';
import { Form, Input, Button, Select, message } from 'antd';
import { UserOutlined, LockOutlined, PhoneOutlined, GlobalOutlined } from '@ant-design/icons';
import BrandLogo, { BRAND_NAME } from './BrandLogo';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import './Login.css';

const Login = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { login } = useAuth();
    const { t, i18n } = useTranslation();
    const [form] = Form.useForm();

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const response = await login(values.phoneNumber, values.password);
            message.success(t('success'));

            // Change language based on user preference
            if (response.user.language) {
                i18n.changeLanguage(response.user.language);
            }

            // Navigate based on role
            switch (response.user.role) {
                case 'superadmin':
                    navigate('/superadmin/dashboard');
                    break;
                case 'hospitaladmin':
                    navigate('/hospitaladmin/dashboard');
                    break;
                case 'lab':
                    navigate('/lab/tests');
                    break;
                case 'pharmacy':
                    navigate('/pharmacy/prescriptions');
                    break;
                case 'optician':
                    navigate('/optician/orders');
                    break;
                case 'medicalseller':
                    navigate('/medicalseller/dashboard');
                    break;
                case 'companyseller':
                    navigate('/companyseller/dashboard');
                    break;
                case 'doctor':
                    navigate('/doctor/dashboard');
                    break;
                case 'secretary':
                    navigate('/secretary/appointments');
                    break;
                default:
                    navigate('/');
            }
        } catch (error) {
            // No response at all means the API host was never reached - offline, DNS,
            // CORS, or a bundle built against the wrong API URL. Say that plainly:
            // a bare "Error" is indistinguishable from a wrong password.
            const serverMessage = error.response?.data?.message;
            message.error(serverMessage || (error.response ? t('error') : t('connectionError')));
        } finally {
            setLoading(false);
        }
    };

    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang);
        localStorage.setItem('language', lang);
    };

    return (
        <div className="login-container">
            <div className="login-wrapper">
                        <div className="login-header">
                            <div className="logo-container">
                                <BrandLogo size={84} />
                            </div>
                            <h1 className="brand-title">{BRAND_NAME}</h1>
                            <p className="brand-subtitle">Healthcare Management System</p>
                        </div>

                <div className="login-card">
                                <h2 className="welcome-text">{t('login')}</h2>
                                <p className="welcome-subtext">Enter your credentials to continue</p>

                                <div className="language-selector">
                                    <GlobalOutlined className="language-icon" />
                                    <Select
                                        defaultValue={i18n.language}
                                        onChange={changeLanguage}
                                        suffixIcon={null}
                                        className="language-select"
                                    >
                                        <Select.Option value="en">English</Select.Option>
                                        <Select.Option value="ar">العربية</Select.Option>
                                        <Select.Option value="ku">کوردی</Select.Option>
                                    </Select>
                                </div>

                                <Form
                                    form={form}
                                    name="login"
                                    onFinish={onFinish}
                                    layout="vertical"
                                    size="large"
                                    autoComplete="off"
                                >
                                    <Form.Item
                                        name="phoneNumber"
                                        label={t('phoneNumber')}
                                        rules={[
                                            { required: true, message: t('phoneNumber') + ' ' + t('required') },
                                            { pattern: /^[0-9]{10,}$/, message: 'Please enter a valid phone number' }
                                        ]}
                                        normalize={(value) => (value || '').replace(/[\s\-()]/g, '')}
                                    >
                                        <Input
                                            prefix={<PhoneOutlined className="input-icon" />}
                                            placeholder="Enter your phone number"
                                            type="tel"
                                            inputMode="numeric"
                                            autoComplete="tel"
                                            className="custom-input"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="password"
                                        label={t('password')}
                                        rules={[
                                            { required: true, message: t('password') + ' ' + t('required') }
                                        ]}
                                    >
                                        <Input.Password
                                            prefix={<LockOutlined className="input-icon" />}
                                            placeholder="Enter your password"
                                            className="custom-input"
                                        />
                                    </Form.Item>

                                    <Form.Item style={{ marginBottom: 0 }}>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={loading}
                                            block
                                            size="large"
                                            className="login-button"
                                        >
                                            {loading ? 'Signing In...' : t('login')}
                                        </Button>
                                    </Form.Item>
                                </Form>
                </div>

                <div className="login-footer">
                    <p>&copy; 2026 {BRAND_NAME}. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
};

export default Login;
